import { CartLinesUpdateEvent } from '@shopify/events';

class EbookLanding extends HTMLElement {
  connectedCallback() {
    document.documentElement.classList.add('ebook-landing-active');
    document.body?.classList.add('ebook-landing-active');

    this.forms = [...this.querySelectorAll('.ebook-product-form')];
    this.forms.forEach((form) => form.addEventListener('submit', this.onSubmit.bind(this)));

    this.querySelectorAll('[data-ebook-cta]').forEach((button) => {
      button.addEventListener('click', () => {
        window.dispatchEvent(
          new CustomEvent('ebook:cta-click', {
            detail: {
              placement: button.dataset.ebookCta,
              productId: this.dataset.productId || null,
            },
          })
        );
      });
    });

    this.trackVisit();
    this.initCarousels();
  }

  trackVisit() {
    if (this.dataset.visitCounterEnabled !== 'true' || this.dataset.themeEditor === 'true') return;

    const counterKey = this.dataset.visitCounterKey?.trim();
    if (!counterKey) return;

    const sessionKey = `ebook-visit-counted:${counterKey}`;

    try {
      if (window.sessionStorage.getItem(sessionKey)) return;
      window.sessionStorage.setItem(sessionKey, 'true');
    } catch {
      return;
    }

    this.visitCounterPixel = new Image(1, 1);
    this.visitCounterPixel.referrerPolicy = 'strict-origin-when-cross-origin';
    this.visitCounterPixel.src =
      `https://countapi.mileshilliard.com/api/v1/hit/${encodeURIComponent(counterKey)}?t=${Date.now()}`;
  }

  initCarousels() {
    this.carouselObservers = [];
    this.carouselTimers = [];

    this.querySelectorAll('[data-ebook-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-ebook-carousel-track]');
      const previousButton = carousel.querySelector('[data-ebook-carousel-prev]');
      const nextButton = carousel.querySelector('[data-ebook-carousel-next]');
      const progress = carousel.querySelector('[data-ebook-carousel-progress]');

      if (!track) return;

      const cards = [...track.querySelectorAll('.ebook-recipe-card, .ebook-review-card')];
      let stopAutoplay = () => {};
      let startAutoplay = () => {};
      let restartTimer;

      const getStep = () => {
        const card = cards[0];
        const styles = getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.86;
      };

      const update = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const ratio = maxScroll > 0 ? Math.min(1, Math.max(0, track.scrollLeft / maxScroll)) : 0;
        const visibleRatio = Math.min(1, track.clientWidth / track.scrollWidth);
        const thumbWidth = Math.max(24, visibleRatio * 100);

        if (previousButton) previousButton.disabled = track.scrollLeft <= 2;
        if (nextButton) nextButton.disabled = track.scrollLeft >= maxScroll - 2;

        if (progress) {
          progress.style.width = `${thumbWidth}%`;
          progress.style.left = `${ratio * (100 - thumbWidth)}%`;
        }
      };

      const scrollToIndex = (index, behavior = 'smooth') => {
        const step = getStep();
        const maxIndex = Math.max(0, cards.length - 1);
        const safeIndex = Math.min(maxIndex, Math.max(0, index));
        track.scrollTo({ left: safeIndex * step, behavior });
      };

      const move = (direction) => {
        const step = getStep();
        const currentIndex = step > 0 ? Math.round(track.scrollLeft / step) : 0;
        scrollToIndex(currentIndex + direction);
      };

      previousButton?.addEventListener('click', () => move(-1));
      nextButton?.addEventListener('click', () => move(1));

      let frame;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(update);
        },
        { passive: true }
      );

      let activePointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let dragged = false;

      const finishDrag = (event, cancelled = false) => {
        if (activePointerId === null || event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        const step = getStep();
        const startIndex = step > 0 ? Math.round(startScrollLeft / step) : 0;
        const currentIndex = step > 0 ? Math.round(track.scrollLeft / step) : 0;
        let targetIndex = currentIndex;

        if (!cancelled && Math.abs(deltaX) > 34) {
          targetIndex = startIndex + (deltaX < 0 ? 1 : -1);
        }

        if (track.hasPointerCapture?.(activePointerId)) {
          track.releasePointerCapture(activePointerId);
        }

        activePointerId = null;
        track.classList.remove('is-dragging');
        scrollToIndex(targetIndex);
        window.clearTimeout(restartTimer);
        restartTimer = window.setTimeout(startAutoplay, 900);
      };

      track.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button > 0) return;

        activePointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = track.scrollLeft;
        dragged = false;
        stopAutoplay();
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      });

      track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) > 5) dragged = true;
        if (!dragged) return;

        event.preventDefault();
        track.scrollLeft = startScrollLeft - deltaX;
      });

      track.addEventListener('pointerup', (event) => finishDrag(event));
      track.addEventListener('pointercancel', (event) => finishDrag(event, true));
      track.addEventListener(
        'click',
        (event) => {
          if (!dragged) return;
          event.preventDefault();
          event.stopPropagation();
          dragged = false;
        },
        true
      );

      track.querySelectorAll('img').forEach((image) => {
        image.addEventListener('dragstart', (event) => event.preventDefault());
      });

      const observer = new ResizeObserver(update);
      observer.observe(track);
      this.carouselObservers.push(observer);
      requestAnimationFrame(update);

      if (carousel.dataset.ebookAutoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let timer;

        stopAutoplay = () => {
          if (timer) window.clearInterval(timer);
          timer = undefined;
        };

        startAutoplay = () => {
          stopAutoplay();
          timer = window.setInterval(() => {
            const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
            if (maxScroll <= 2 || document.hidden || activePointerId !== null) return;

            if (track.scrollLeft >= maxScroll - 2) {
              scrollToIndex(0);
            } else {
              move(1);
            }
          }, 4600);
        };

        carousel.addEventListener('pointerenter', stopAutoplay);
        carousel.addEventListener('pointerleave', startAutoplay);
        carousel.addEventListener('focusin', stopAutoplay);
        carousel.addEventListener('focusout', startAutoplay);

        startAutoplay();
        this.carouselTimers.push(() => {
          stopAutoplay();
          window.clearTimeout(restartTimer);
        });
      }
    });
  }

  disconnectedCallback() {
    this.carouselObservers?.forEach((observer) => observer.disconnect());
    this.carouselTimers?.forEach((stopTimer) => stopTimer());

    requestAnimationFrame(() => {
      if (!document.querySelector('ebook-landing.ebook-page')) {
        document.documentElement.classList.remove('ebook-landing-active');
        document.body?.classList.remove('ebook-landing-active');
      }
    });
  }

  async onSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('.ebook-form-error');

    if (!button || button.disabled) return;

    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    if (error) error.textContent = '';

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    if (sectionIds.length > 0) {
      formData.set('sections', [...new Set(sectionIds)].join(','));
    }

    const cartUpdate = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(variantId),
            quantity,
          },
        ],
        promise: cartUpdate.promise,
      })
    );

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Das Produkt konnte nicht hinzugefügt werden.');
      }

      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!cartResponse.ok) {
        throw new Error('Der Warenkorb konnte nicht aktualisiert werden.');
      }

      const cart = await cartResponse.json();

      cartUpdate.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ebook-landing',
          sourceId: this.id,
          itemCount: quantity,
          productId: this.dataset.productId || null,
          sections: result.sections,
          didError: false,
        },
      });

      window.dispatchEvent(
        new CustomEvent('ebook:add-to-cart', {
          detail: {
            variantId: result.variant_id || result.id,
            productId: this.dataset.productId || null,
          },
        })
      );

      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');

      await customElements.whenDefined('theme-drawer');
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const cartDrawer = document.querySelector('#cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        window.location.assign(this.dataset.cartUrl);
      }
    } catch (submitError) {
      cartUpdate.reject(submitError);
      if (error) error.textContent = submitError.message;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }

}

if (!customElements.get('ebook-landing')) {
  customElements.define('ebook-landing', EbookLanding);
}
